import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { getAuthErrorMessage } from '../utils/authErrorUtils';
import { ADMIN_PATHS, adminPath } from '../constants/adminPaths';
import { fetchAdminSetupAvailable } from '../api/adminSetupApi';
import '../admin-auth/styles/adminLoginPage.css';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [setupAvailable, setSetupAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [secondFactorMethod, setSecondFactorMethod] = useState('');
  const [maskedDestination, setMaskedDestination] = useState('');
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsError, setSmsError] = useState('');
  const lastAutoSubmittedCodeRef = useRef('');
  const { signInWithEmailLink, verifyPendingSmsCode, getPendingSecondFactorInfo, admin, pendingMagicLinkLogin } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    fetchAdminSetupAvailable()
      .then((available) => {
        if (!cancelled) setSetupAvailable(available);
      })
      .catch(() => {
        if (!cancelled) setSetupAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // If the user completes magic-link sign-in in another tab, auto-redirect.
  useEffect(() => {
    if (!admin) return;
    navigate(adminPath(ADMIN_PATHS.DASHBOARD), { replace: true });
  }, [admin, navigate]);

  useEffect(() => {
    if (!pendingMagicLinkLogin) return;
    const pending = getPendingSecondFactorInfo();
    setSecondFactorMethod(pending?.secondFactorMethod === 'sms' ? 'sms' : pending?.secondFactorMethod === 'email' ? 'email' : '');
    setMaskedDestination(pending?.maskedDestination || '');
    setLinkSent(true);
    if (pending?.secondFactorMethod === 'sms') {
      setShowSmsModal(true);
    }
  }, [pendingMagicLinkLogin, getPendingSecondFactorInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pendingMagicLinkLogin) {
      const pending = getPendingSecondFactorInfo();
      setSecondFactorMethod(pending?.secondFactorMethod === 'sms' ? 'sms' : pending?.secondFactorMethod === 'email' ? 'email' : '');
      setMaskedDestination(pending?.maskedDestination || '');
      setLinkSent(true);
      return;
    }
    setLoading(true);
    setError('');
    setLinkSent(false);
    setMaskedDestination('');
    setSmsCode('');
    setShowSmsModal(false);
    setSmsError('');
    lastAutoSubmittedCodeRef.current = '';

    try {
      const res = await signInWithEmailLink(email, password);
      if (res?.magicLinkSent) {
        setSecondFactorMethod(res?.secondFactorMethod === 'sms' ? 'sms' : res?.secondFactorMethod === 'email' ? 'email' : '');
        setMaskedDestination(res?.maskedDestination || '');
        setLinkSent(true);
        if (res?.secondFactorMethod === 'sms') {
          setShowSmsModal(true);
        }
        return;
      }
    } catch (err) {
      setLinkSent(false);
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const submitSmsCode = async (codeValue) => {
    setLoading(true);
    setError('');
    setSmsError('');
    try {
      await verifyPendingSmsCode(codeValue);
    } catch (err) {
      setLinkSent(false);
      setSmsError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSmsVerify = async (e) => {
    e.preventDefault();
    await submitSmsCode(smsCode);
  };

  useEffect(() => {
    if (!showSmsModal || secondFactorMethod !== 'sms' || loading) return;
    if (!/^\d{6}$/.test(smsCode)) return;
    if (lastAutoSubmittedCodeRef.current === smsCode) return;
    lastAutoSubmittedCodeRef.current = smsCode;
    void submitSmsCode(smsCode);
  }, [smsCode, showSmsModal, secondFactorMethod, loading]);

  return (
    <div className="adminLogin">
      <div className="adminLogin__bg" aria-hidden="true" />
      <main className="adminLogin__container">
        <section className="adminLogin__card" aria-label="Admin login">
          <header className="adminLogin__header">
            <div className="adminLogin__badge" aria-hidden="true">
              Admin
            </div>
            <h1 className="adminLogin__title">Sign in</h1>
            <p className="adminLogin__subtitle">Enter your admin credentials to continue.</p>
          </header>

          {error ? (
            <div className="adminLogin__alert" role="alert">
              <span className="adminLogin__alertDot" aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          {!error && linkSent ? (
            <div className="adminLogin__alert" role="status" style={{ borderColor: 'rgba(47, 124, 255, 0.35)', background: 'rgba(47, 124, 255, 0.12)' }}>
              <span className="adminLogin__alertDot" aria-hidden="true" style={{ background: 'var(--al-accent)' }} />
              <span>
                {secondFactorMethod === 'sms'
                  ? `SMS code sent${maskedDestination ? ` to ${maskedDestination}` : ''}.`
                  : secondFactorMethod === 'email'
                    ? 'Login link sent. Check your email and click the link to finish signing in. You can keep this page open — it will log you in automatically.'
                    : 'Verification request sent. Please wait for your second-factor prompt.'}
              </span>
            </div>
          ) : null}

          {linkSent && secondFactorMethod === 'sms' && !showSmsModal ? (
            <div className="adminLogin__codeTriggerWrap">
              <button
                className="adminLogin__submit adminLogin__codeTrigger"
                type="button"
                onClick={() => {
                  setSmsError('');
                  lastAutoSubmittedCodeRef.current = '';
                  setShowSmsModal(true);
                }}
              >
                Enter Code
              </button>
            </div>
          ) : null}

          <form className="adminLogin__form" onSubmit={handleSubmit}>
            <label className="adminLogin__label">
              <span className="adminLogin__labelText">Email</span>
              <input
                className="adminLogin__input"
                type="text"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </label>

            <label className="adminLogin__label">
              <span className="adminLogin__labelRow">
                <span className="adminLogin__labelText">Password</span>
                <button
                  type="button"
                  className="adminLogin__linkBtn"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </span>
              <input
                className="adminLogin__input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
              />
            </label>

            <div className="adminLogin__linkRow">
              <RouterLink className="adminLogin__linkBtn" to={adminPath(ADMIN_PATHS.FORGOT_PASSWORD)}>
                Forgot password?
              </RouterLink>
            </div>

            <button className="adminLogin__submit" type="submit" disabled={loading || pendingMagicLinkLogin}>
              {loading ? (
                <>
                  <span className="adminLogin__spinner" aria-hidden="true" />
                  Sending verification…
                </>
              ) : (
                pendingMagicLinkLogin ? 'Check verification…' : 'Sign in'
              )}
            </button>
          </form>

          <footer className="adminLogin__footer">
            {setupAvailable ? (
              <RouterLink className="adminLogin__secondaryLink" to={adminPath(ADMIN_PATHS.SETUP)}>
                First time? Create the admin account
              </RouterLink>
            ) : null}
            <RouterLink className="adminLogin__secondaryLink" to={adminPath(ADMIN_PATHS.SUPPORT_LOGIN)}>
              Support employee? Use Support Login
            </RouterLink>
          </footer>
        </section>
      </main>

      {showSmsModal && secondFactorMethod === 'sms' ? (
        <div
          className="adminLogin__modalBackdrop"
          role="presentation"
          onClick={() => {
            setSmsError('');
            lastAutoSubmittedCodeRef.current = '';
            setShowSmsModal(false);
          }}
        >
          <section
            className="adminLogin__modal"
            role="dialog"
            aria-modal="true"
            aria-label="SMS verification"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="adminLogin__modalHeader">
              <h2 className="adminLogin__modalTitle">Verify SMS code</h2>
              <button
                type="button"
                className="adminLogin__modalClose"
                onClick={() => {
                  setSmsError('');
                  lastAutoSubmittedCodeRef.current = '';
                  setShowSmsModal(false);
                }}
                aria-label="Close verification popup"
              >
                X
              </button>
            </header>
            <p className="adminLogin__modalText">
              Enter the 6-digit code sent{maskedDestination ? ` to ${maskedDestination}` : ''}.
            </p>
            {smsError ? (
              <div className="adminLogin__alert" role="alert">
                <span className="adminLogin__alertDot" aria-hidden="true" />
                <span>{smsError}</span>
              </div>
            ) : null}
            <form className="adminLogin__form" onSubmit={handleSmsVerify}>
              <label className="adminLogin__label">
                <span className="adminLogin__labelText">SMS verification code</span>
                <input
                  className="adminLogin__input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={smsCode}
                  onChange={(e) => setSmsCode(String(e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  required
                />
              </label>
              <button className="adminLogin__submit" type="submit" disabled={loading}>
                {loading ? 'Verifying code…' : 'Verify SMS code'}
              </button>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default AdminLogin; 